import { LANGUAGE_REGEX } from '@utils/variables';
import Image from 'next/image';
import Link from 'next/link';
import { FC } from 'react';
import ParagraphWithInternalLinks from 'src/components/common/ParagraphWithInternalLinks';
import DisplayCode from 'src/features/devHub/features/devHubSingleQuest/components/DisplayCode';
import List from 'src/features/devHub/features/devHubSingleQuest/components/List';
import Quote from 'src/features/devHub/features/devHubSingleQuest/components/Quote';
import Warning from 'src/features/devHub/features/devHubSingleQuest/components/Warning';
import { getHtmlTags } from 'src/functions/getHtmlTags';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGGradientSeparator from 'src/styles/theme/components/NGGradientSeparator';
import NGHeading from 'src/styles/theme/components/NGHeading';
import NGText from 'src/styles/theme/components/NGText';
import { EFontStyle, ETextAlign, ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { ESize, ETextColor } from 'src/styles/theme/utils/enums';
import { getEnumFromValue } from 'src/styles/theme/utils/functions';
import ImageMagnifier from '../ImageMagnifier';
import { EStrapiRichText } from './StrapiRichText.enum';
import { NGTableEx, StyledTableRichText } from './StrapiRichText.styles';
import { IStrapiRichText } from './StrapiRichText.type';

const displayStyle = { display: 'block' };

const StrapiRichText: FC<IStrapiRichText> = ({
	type,
	data,
	contentTagUpdated = false,
	isFirst,
	headerColor = ETextColor.white,
	paragraphColor = ETextColor.white,
	paragraphSize = ESize.m,
	paragraphPaddingTop = ESize['2xs'],
	paragraphPaddingBottom = ESize['2xs'],
	paragraphPaddingRight = ESize['2xs'],
	paragraphPaddingLeft = ESize.unset,
	headerPaddingRight = ESize['2xs'],
	internalLinkPaddingBottom = ESize.unset
}) => {
	switch (type) {
		case EStrapiRichText.quote:
			return <Quote quote={data.text} quoteAuthor={data.caption} img={data.img} />;
		case EStrapiRichText.paragraph:
			return (
				<NGText
					className='richtext'
					paddingTop={isFirst ? undefined : paragraphPaddingTop}
					paddingBottom={paragraphPaddingBottom}
					paddingRight={paragraphPaddingRight}
					paddingLeft={paragraphPaddingLeft}
					size={paragraphSize}
					textColor={paragraphColor}
				>
					{getHtmlTags(data.text)}
				</NGText>
			);
		case EStrapiRichText.warning:
			return <Warning title={data.title} message={getHtmlTags(data.message, false)} />;
		case EStrapiRichText.delimiter:
			return <NGGradientSeparator marginY={ESize.s} />;
		case EStrapiRichText.image:
			const srcToUse = data.file?.url;

			return srcToUse ? (
				<Flex
					direction={EFlex.column}
					paddingY={ESize['xs']}
					fullWidth
					horizontal={EFlex.center}
					vertical={EFlex.center}
					paddingRight={ESize['2xs']}
				>
					<ImageMagnifier>
						<Image
							src={srcToUse}
							alt={data.file?.alt}
							width={data.file?.width}
							height={data.file?.height}
							draggable={false}
						/>
					</ImageMagnifier>

					{data.caption ? (
						<NGText
							size={ESize.m}
							textColor={ETextColor.grey}
							textAlign={ETextAlign.center}
							fontStyle={EFontStyle.italic}
							marginTop={ESize['3xs']}
						>
							{getHtmlTags(data.caption)}
						</NGText>
					) : null}
				</Flex>
			) : null;
		case EStrapiRichText.header:
			return (
				<NGHeading
					id={data?.id}
					className='richtext'
					type={getEnumFromValue(`h${data.level}`, ETextType)}
					textColor={headerColor}
					textAlign={ETextAlign.left}
					paddingTop={isFirst ? undefined : ESize['xs']}
					paddingRight={headerPaddingRight}
					paddingBottom={ESize['2xs']}
					style={displayStyle}
					singleLine={false}
				>
					{getHtmlTags(data.text)}
				</NGHeading>
			);
		case EStrapiRichText.list:
			return <List style={data.style} items={data.items} contentTagUpdated={contentTagUpdated} />;
		case EStrapiRichText.code:
			if (!data.code) return null;

			// dete if the code start by #lang:regex# and where regex can be any string without #
			// if yes, we remove the #lang:regex# and extract the regex
			const language = data.code.match(LANGUAGE_REGEX) ? data.code.match(LANGUAGE_REGEX)?.[1] : undefined;

			// remove the #lang:regex# from the code
			let code = data.code.replace(LANGUAGE_REGEX, '');

			// remove the first \n from the code
			if (code[0] === '\n') code = code.slice(1);

			return <DisplayCode code={code} language={language} />;
		case EStrapiRichText.table:
			try {
				const headers = data.content?.[0].map((cell: string, i) => ({
					label: cell,
					getValue: (row: any) => row[`row-${i}`].label
				}));

				const rows = data.content?.slice(1).map((row: string[], i) => {
					const obj: {
						[key: string]: {
							id: string;
							label: string;
						};
					} = {};

					row.forEach((cell, j) => {
						obj[`row-${j}`] = {
							label: cell,
							id: `${i}-${j}`
						};
					});

					return obj;
				});

				return (
					<StyledTableRichText marginBottom={ESize['2xs']}>
						<NGTableEx
							headers={headers || []}
							data={rows || []}
							getId={(row) => [...Object.keys(row), row['row-0']?.id].join('-')}
							checkHtmlTags
							forceSelectedRows
							headerAlwaysVisible
							deactivateSwipOnTouch
						/>
					</StyledTableRichText>
				);
			} catch {
				return null;
			}
		case EStrapiRichText.internalLink:
			return (
				<NGText paddingBottom={internalLinkPaddingBottom}>
					<Link href={data.href as string}>
						<a className='richtext rt-link'>{data.text}</a>
					</Link>
				</NGText>
			);
		case EStrapiRichText.paragraphWithLinks:
			return (
				<ParagraphWithInternalLinks
					data={data.textAndInternalLinks || []}
					isFirst={isFirst}
					paddingTop={paragraphPaddingTop}
					paddingBottom={paragraphPaddingBottom}
					paddingRight={paragraphPaddingRight}
					paddingLeft={paragraphPaddingLeft}
					size={paragraphSize}
					color={paragraphColor}
				/>
			);
		default:
			return null;
	}
};

export { StrapiRichText };
